import type { GameSpeed, PropertyType, LocationId } from '../game/types';

/**
 * Gets the speed multiplier for a given game speed
 * @param speed The game speed string
 * @returns The multiplier value (0 for pause, 1 for 1x, 2 for 2x, 4 for 4x)
 */
export function getSpeedMultiplier(speed: GameSpeed): number {
  switch (speed) {
    case 'pause': return 0;
    case '1x': return 1;
    case '2x': return 2;
    case '4x': return 4;
    default: return 1;
  }
}

/**
 * Fisher-Yates shuffle algorithm to randomly shuffle an array
 * @param array The array to shuffle
 * @returns A new shuffled array (original array is not modified)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]; // Create a copy
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Maps a PropertyType to its corresponding letter code
 * A = Lethal, B = Memetic, C = Antiepistemic, D = Breaching, E = Hyperreal, F = Dormant
 */
export function propertyToLetter(property: PropertyType): string {
  const mapping: Record<PropertyType, string> = {
    'Lethal': 'A',
    'Memetic': 'B',
    'Antiepistemic': 'C',
    'Breaching': 'D',
    'Hyperreal': 'E',
    'Dormant': 'F'
  };
  return mapping[property];
}

/**
 * Generates an anomaly code from a properties array
 * @param properties The properties array (in the order they should appear in the code)
 * @returns A 3-letter code string
 */
export function generateCodeFromProperties(properties: PropertyType[]): string {
  return properties.map(propertyToLetter).join('');
}

